type RequestOptions = {
  timeoutMs?: number
}

type WaitChannelOptions = {
  timeoutMs?: number
  pollMs?: number
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10000
const DEFAULT_WAIT_CHANNEL_TIMEOUT_MS = 3500
const DEFAULT_WAIT_CHANNEL_POLL_MS = 50

let inFlightTokenRequest: Promise<string | null> | null = null

function normalizeToken(token: string | null | undefined): string | null {
  const normalized = token?.trim()
  return normalized ? normalized : null
}

export function hasWebviewTokenJsChannel() {
  return Boolean(window.REQUEST_TOKEN)
}

export function waitForWebviewTokenJsChannel(options: WaitChannelOptions = {}): Promise<boolean> {
  if (hasWebviewTokenJsChannel()) return Promise.resolve(true)

  const timeoutMs = options.timeoutMs ?? DEFAULT_WAIT_CHANNEL_TIMEOUT_MS
  const pollMs = options.pollMs ?? DEFAULT_WAIT_CHANNEL_POLL_MS

  return new Promise<boolean>((resolve) => {
    const startedAt = Date.now()

    const tick = () => {
      if (hasWebviewTokenJsChannel()) {
        resolve(true)
        return
      }

      if (Date.now() - startedAt >= timeoutMs) {
        resolve(false)
        return
      }

      window.setTimeout(tick, pollMs)
    }

    tick()
  })
}

export async function requestWebviewTokenViaJsChannel(
  options: RequestOptions = {}
): Promise<string | null> {
  const tokenChannel = window.REQUEST_TOKEN
  if (!tokenChannel) return null

  if (inFlightTokenRequest) return inFlightTokenRequest

  const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS
  const previousOnReceiveToken = window.onReceiveToken

  inFlightTokenRequest = new Promise<string | null>((resolve) => {
    let isSettled = false

    const restoreHandler = () => {
      window.onReceiveToken = previousOnReceiveToken
    }

    const settle = (token: string | null) => {
      if (isSettled) return
      isSettled = true
      window.clearTimeout(timeoutId)
      restoreHandler()
      resolve(token)
    }

    window.onReceiveToken = (rawToken: string) => {
      const token = normalizeToken(rawToken)

      if (previousOnReceiveToken) {
        void Promise.resolve(previousOnReceiveToken(rawToken)).catch(() => {
          // Ignore failures in pre-existing handlers and continue current request flow.
        })
      }

      settle(token)
    }

    const timeoutId = window.setTimeout(() => {
      settle(null)
    }, timeoutMs)

    try {
      tokenChannel.postMessage('request')
    } catch {
      settle(null)
    }
  }).finally(() => {
    inFlightTokenRequest = null
  })

  return inFlightTokenRequest
}
